import { useEffect, useMemo, useState } from 'react';

import dynamic from 'next/dynamic';

import Typography from '@components/Typography';
import { useDevice } from '@contexts/device';
import CustomIcon from '@components/utils/CustomIcon';
import { InfoIcon } from '@assets/icons';
import { FlightViewModel, PackageTransferViewModel } from '@api/v3';

import FlightItem from './flight/flight-item';
import ReverseAccordion from './reversed-accordion';
import {
  DepatureContainer,
  DepatureContainerHeader,
  Container,
  LandingContainer,
  PersonInfoContainer,
  TransferWrapper,
  HeaderInfoContainer,
  InfoBox,
} from './flight/flight-item/styled';
import TransferItem from './transfer';
import { FLIGHT_DIRECTION, FlightProps, selectFlight } from './helper';
import {
  AccordionMaxHeightBody,
  AccordionOptionsWrapper,
} from './reversed-accordion/styled';

const CampaignFlightInfoModal = dynamic(
  () => import('./flight/campaign-flight-modal'),
  {
    ssr: false,
  }
);

const FlightTransferSelectionComponent = ({
  adultCount,
  childCount,
  transferItems,
  flightItems,
  selectedDepatureFlight,
  setSelectedDepatureFlight,
  selectedReturnFlight,
  setSelectedReturnFlight,
  selectedDepatureTransfer,
  setSelectedDepatureTransfer,
  selectedReturnTransfer,
  setSelectedReturnTransfer,
  isListing = false,
}: {
  adultCount: number;
  childCount: number;
  transferItems?: PackageTransferViewModel[];
  flightItems?: FlightViewModel[];
  selectedDepatureFlight?: FlightProps;
  setSelectedDepatureFlight: (flight) => void;
  selectedReturnFlight?: FlightProps;
  setSelectedReturnFlight: (flight) => void;
  selectedDepatureTransfer?: PackageTransferViewModel;
  setSelectedDepatureTransfer: (transfer) => void;
  selectedReturnTransfer?: PackageTransferViewModel;
  setSelectedReturnTransfer: (transfer) => void;
  isListing?: boolean;
}) => {
  const { isDesktop } = useDevice();
  const [depatureFlightIsOpen, setDepatureFlightIsOpen] =
    useState<boolean>(false);
  const [returnFlightIsOpen, setReturnFlightIsOpen] = useState<boolean>(false);
  const [returnTransferIsOpen, setReturnTransferIsOpen] =
    useState<boolean>(false);
  const [depatureTransferIsOpen, setDepatureTransferIsOpen] =
    useState<boolean>(false);
  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);

  const [minPriceDepatureFlight, setMinPriceDepatureFlight] =
    useState<FlightProps>();
  const [minPriceReturnFlight, setMinPriceReturnFlight] =
    useState<FlightProps>();
  const [minPriceDepatureTransfer, setMinPriceDepatureTransfer] =
    useState<PackageTransferViewModel>();
  const [minPriceReturnTransfer, setMinPriceReturnTransfer] =
    useState<PackageTransferViewModel>();

  const selectedProviderName = useMemo(() => {
    if (
      selectedDepatureFlight?.orginalItem.contracted === true &&
      selectedReturnFlight?.orginalItem.contracted === false
    ) {
      return selectedDepatureFlight?.providerName;
    } else if (
      selectedDepatureFlight?.orginalItem.contracted === false &&
      selectedReturnFlight?.orginalItem.contracted === true
    ) {
      return selectedReturnFlight?.providerName;
    }
  }, [selectedDepatureFlight, selectedReturnFlight]);

  const departureFlights = useMemo(() => {
    if (flightItems) {
      return flightItems.filter((option) => {
        return option.flights[0].legNumber === FLIGHT_DIRECTION.DEPARTURE;
      });
    }
  }, [flightItems]);
  const returnFlights = useMemo(() => {
    if (flightItems) {
      return flightItems.filter((option) => {
        return option.flights[0].legNumber === FLIGHT_DIRECTION.RETURN;
      });
    }
  }, [flightItems]);

  const departureFlightListWithSummaries: FlightProps[] = useMemo(
    () =>
      departureFlights?.map((option): FlightProps => selectFlight(option)) ||
      [],
    [departureFlights]
  );
  const returnFlightListWithSummaries: FlightProps[] = useMemo(
    () =>
      returnFlights?.map((option): FlightProps => selectFlight(option)) || [],
    [returnFlights]
  );

  const depatureTransfers = useMemo(
    () => transferItems?.filter((item) => item.transferType === 'Departure'),
    [transferItems]
  );
  const returnTransfers = useMemo(
    () => transferItems?.filter((item) => item.transferType === 'Return'),
    [transferItems]
  );
  const handleSelectDepartureFlight = (value: FlightProps) => {
    setDepatureFlightIsOpen(!depatureFlightIsOpen);
    setSelectedDepatureFlight(value);
  };
  const handleSelectReturnFlight = (value: FlightProps) => {
    setReturnFlightIsOpen(!returnFlightIsOpen);
    setSelectedReturnFlight(value);
  };
  const handleSelectDepartureTransfer = (value: PackageTransferViewModel) => {
    setDepatureTransferIsOpen(!depatureTransferIsOpen);
    setSelectedDepatureTransfer(value);
  };

  const handleSelectReturnTransfer = (value: PackageTransferViewModel) => {
    setReturnTransferIsOpen(!returnTransferIsOpen);
    setSelectedReturnTransfer(value);
  };

  useEffect(() => {
    const item = departureFlightListWithSummaries?.find(
      (item) => item?.orginalItem?.selected === true
    );
    if (item) {
      setSelectedDepatureFlight(item);
      if (isListing) {
        setMinPriceDepatureFlight(item);
      } else {
        setMinPriceDepatureFlight(departureFlightListWithSummaries[0]);
      }
    } else {
      setSelectedDepatureFlight(departureFlightListWithSummaries[0]);
      setMinPriceDepatureFlight(departureFlightListWithSummaries[0]);
    }
    setMinPriceDepatureFlight(departureFlightListWithSummaries[0]);
  }, [departureFlightListWithSummaries]);

  useEffect(() => {
    const item = returnFlightListWithSummaries?.find(
      (item) => item?.orginalItem?.selected === true
    );
    if (item) {
      setSelectedReturnFlight(item);
      if (isListing) {
        setMinPriceReturnFlight(item);
      } else {
        setMinPriceReturnFlight(returnFlightListWithSummaries[0]);
      }
    } else {
      setSelectedReturnFlight(returnFlightListWithSummaries[0]);
      setMinPriceReturnFlight(returnFlightListWithSummaries[0]);
    }
  }, [returnFlightListWithSummaries]);

  useEffect(() => {
    const transfer = depatureTransfers?.find((item) => item.selected === true);
    if (transfer) {
      setSelectedDepatureTransfer(transfer);
      setMinPriceDepatureTransfer(transfer);
    } else {
      setSelectedDepatureTransfer(depatureTransfers?.[0]);
      setMinPriceDepatureTransfer(depatureTransfers?.[0]);
    }
  }, [depatureTransfers]);

  useEffect(() => {
    const transfer = returnTransfers?.find((item) => item.selected === true);
    if (transfer) {
      setSelectedReturnTransfer(transfer);
      setMinPriceReturnTransfer(transfer);
    } else {
      setSelectedReturnTransfer(returnTransfers?.[0]);
      setMinPriceReturnTransfer(returnTransfers?.[0]);
    }
  }, [returnTransfers]);

  useEffect(() => {
    if (
      selectedDepatureFlight?.orginalItem?.contracted === true &&
      selectedReturnFlight?.orginalItem?.contracted === false
    ) {
      if (
        selectedReturnFlight?.providerName !==
        selectedDepatureFlight?.providerName
      ) {
        setShowInfoModal(true);
      }
    } else if (
      selectedDepatureFlight?.orginalItem?.contracted === false &&
      selectedReturnFlight?.orginalItem?.contracted === true
    ) {
      if (
        selectedReturnFlight?.providerName !==
        selectedDepatureFlight?.providerName
      ) {
        setShowInfoModal(true);
      }
    }
  }, [selectedDepatureFlight, selectedReturnFlight]);

  const closeInfoModal = () => {
    setShowInfoModal(false);
  };

  return selectedReturnFlight && selectedDepatureFlight ? (
    <Container isListing={isListing}>
      <InfoBox isListing={isListing}>
        <CustomIcon
          icon={InfoIcon}
          fontSize={18}
          paletteColor='clearBlue'
          height={18}
        />
        <Typography variant='caption2' color='Blue600' weight='Regular'>
          Uçak bileti müsaitlik ve fiyatları anlık olarak değişiklik
          gösterebilir.
        </Typography>
      </InfoBox>
      <DepatureContainer>
        <DepatureContainerHeader>
          <HeaderInfoContainer>
            <Typography
              variant={isDesktop ? 'Title4' : 'Title2'}
              color='D400'
              weight='Bold'
            >
              Gidiş
            </Typography>
            <Typography
              variant={isDesktop ? 'Title4' : 'Title5'}
              color='D400'
              weight='Light'
            >
              {isListing ? 'Uçuş' : 'Uçuş + Transfer'}
            </Typography>
          </HeaderInfoContainer>
          <PersonInfoContainer>
            <Typography
              variant={isDesktop ? 'caption1' : 'caption2'}
              color='D300'
              weight='Light'
            >
              {childCount
                ? `${adultCount + childCount} Yolcu`
                : `${adultCount} Yolcu`}
            </Typography>
          </PersonInfoContainer>
        </DepatureContainerHeader>
        <ReverseAccordion
          title='Diğer Uçuş Seçenekleri'
          isOpen={depatureFlightIsOpen}
          setIsOpen={setDepatureFlightIsOpen}
        >
          <AccordionOptionsWrapper>
            <AccordionMaxHeightBody>
              {depatureFlightIsOpen && departureFlightListWithSummaries ? (
                <>
                  <FlightItem
                    flightItem={selectedDepatureFlight}
                    selectedFlightItem={selectedDepatureFlight}
                    setSelectedFlight={handleSelectDepartureFlight}
                    isListing={isListing}
                    minPriceItem={minPriceDepatureFlight}
                  />
                  {departureFlightListWithSummaries
                    ?.filter(
                      (flight) =>
                        flight.flights !== selectedDepatureFlight?.flights
                    )
                    ?.map((item, index) => {
                      return (
                        <FlightItem
                          key={index}
                          flightItem={item}
                          selectedFlightItem={selectedDepatureFlight}
                          setSelectedFlight={handleSelectDepartureFlight}
                          isListing={isListing}
                          minPriceItem={minPriceDepatureFlight}
                        />
                      );
                    })}
                </>
              ) : (
                <FlightItem
                  flightItem={selectedDepatureFlight}
                  selectedFlightItem={selectedDepatureFlight}
                  setSelectedFlight={handleSelectDepartureFlight}
                  isListing={isListing}
                  minPriceItem={minPriceDepatureFlight}
                />
              )}
            </AccordionMaxHeightBody>
          </AccordionOptionsWrapper>
        </ReverseAccordion>
        {isListing || !selectedDepatureTransfer ? null : (
          <TransferWrapper>
            <ReverseAccordion
              title='Diğer Transfer Seçenekleri'
              isOpen={depatureTransferIsOpen}
              setIsOpen={setDepatureTransferIsOpen}
            >
              {depatureTransferIsOpen && depatureTransfers ? (
                <>
                  <TransferItem
                    transfer={selectedDepatureTransfer}
                    selectedTransferItem={selectedDepatureTransfer}
                    setSelectedTransfer={handleSelectDepartureTransfer}
                    isDeparture={true}
                    minPriceItem={minPriceDepatureTransfer}
                  />
                  {depatureTransfers
                    ?.filter(
                      (transfer) =>
                        transfer.productId !==
                        selectedDepatureTransfer?.productId
                    )
                    .map((item) => {
                      return (
                        <TransferItem
                          key={item?.id}
                          transfer={item}
                          selectedTransferItem={selectedDepatureTransfer}
                          setSelectedTransfer={handleSelectDepartureTransfer}
                          isDeparture={true}
                          minPriceItem={minPriceDepatureTransfer}
                        />
                      );
                    })}
                </>
              ) : (
                <TransferItem
                  transfer={selectedDepatureTransfer}
                  selectedTransferItem={selectedDepatureTransfer}
                  setSelectedTransfer={handleSelectDepartureTransfer}
                  isDeparture={true}
                  minPriceItem={minPriceDepatureTransfer}
                />
              )}
            </ReverseAccordion>
          </TransferWrapper>
        )}
      </DepatureContainer>
      <LandingContainer>
        <DepatureContainerHeader>
          <HeaderInfoContainer>
            <Typography
              variant={isDesktop ? 'Title4' : 'Title2'}
              color='D400'
              weight='Bold'
            >
              Dönüş
            </Typography>
            <Typography
              variant={isDesktop ? 'Title4' : 'Title5'}
              color='D400'
              weight='Light'
            >
              {isListing ? 'Uçuş' : 'Uçuş + Transfer'}
            </Typography>
          </HeaderInfoContainer>
          <PersonInfoContainer>
            <Typography
              variant={isDesktop ? 'caption1' : 'caption2'}
              color='D300'
              weight='Light'
            >
              {childCount
                ? `${adultCount + childCount} Yolcu`
                : `${adultCount} Yolcu`}
            </Typography>
          </PersonInfoContainer>
        </DepatureContainerHeader>
        <ReverseAccordion
          title='Diğer Uçuş Seçenekleri'
          isOpen={returnFlightIsOpen}
          setIsOpen={setReturnFlightIsOpen}
        >
          <AccordionOptionsWrapper>
            <AccordionMaxHeightBody>
              {returnFlightIsOpen && returnFlightListWithSummaries ? (
                <>
                  <FlightItem
                    flightItem={selectedReturnFlight}
                    selectedFlightItem={selectedReturnFlight}
                    setSelectedFlight={handleSelectReturnFlight}
                    isListing={isListing}
                    minPriceItem={minPriceReturnFlight}
                  />
                  {returnFlightListWithSummaries
                    ?.filter(
                      (flight) =>
                        flight.flights !== selectedReturnFlight?.flights
                    )
                    ?.map((item, index) => {
                      return (
                        <FlightItem
                          key={index}
                          flightItem={item}
                          selectedFlightItem={selectedReturnFlight}
                          setSelectedFlight={handleSelectReturnFlight}
                          isListing={isListing}
                          minPriceItem={minPriceReturnFlight}
                        />
                      );
                    })}
                </>
              ) : (
                <FlightItem
                  flightItem={selectedReturnFlight}
                  selectedFlightItem={selectedReturnFlight}
                  setSelectedFlight={handleSelectReturnFlight}
                  isListing={isListing}
                  minPriceItem={minPriceReturnFlight}
                />
              )}
            </AccordionMaxHeightBody>
          </AccordionOptionsWrapper>
        </ReverseAccordion>
        {isListing || !selectedReturnTransfer ? null : (
          <TransferWrapper>
            <ReverseAccordion
              title='Diğer Transfer Seçenekleri'
              isOpen={returnTransferIsOpen}
              setIsOpen={setReturnTransferIsOpen}
            >
              {returnTransferIsOpen && returnTransfers ? (
                <>
                  <TransferItem
                    transfer={selectedReturnTransfer}
                    selectedTransferItem={selectedReturnTransfer}
                    setSelectedTransfer={handleSelectReturnTransfer}
                    minPriceItem={minPriceReturnTransfer}
                  />
                  {returnTransfers
                    ?.filter(
                      (transfer) =>
                        transfer.productId !== selectedReturnTransfer?.productId
                    )
                    .map((item) => {
                      return (
                        <TransferItem
                          key={item?.id}
                          transfer={item}
                          selectedTransferItem={selectedReturnTransfer}
                          setSelectedTransfer={handleSelectReturnTransfer}
                          minPriceItem={minPriceReturnTransfer}
                        />
                      );
                    })}
                </>
              ) : (
                <TransferItem
                  transfer={selectedReturnTransfer}
                  selectedTransferItem={selectedReturnTransfer}
                  setSelectedTransfer={handleSelectReturnTransfer}
                  minPriceItem={minPriceReturnTransfer}
                />
              )}
            </ReverseAccordion>
          </TransferWrapper>
        )}
      </LandingContainer>
      {showInfoModal ? (
        <CampaignFlightInfoModal
          closeInfoModal={closeInfoModal}
          providerName={selectedProviderName}
        />
      ) : null}
    </Container>
  ) : null;
};

export default FlightTransferSelectionComponent;
